
import { Component, signal, computed, effect, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';

interface Phase {
  name: 'Inhale' | 'Hold' | 'Exhale';
  duration: number; // in seconds
}

interface BreathingMode {
  id: string;
  name: string;
  slogan: string;
  description: string;
  steps: string[];
  useCase: string;
  warning?: string;
  phases: Phase[];
}

@Component({
  selector: 'app-deep-breathing-exerciser',
  templateUrl: './deep-breathing-exerciser.component.html',
  styleUrls: ['./deep-breathing-exerciser.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule]
})
export class DeepBreathingExerciserComponent implements OnDestroy {
  private timerInterval: number | undefined;
  private audioContext: AudioContext | null = null;

  modes = signal<BreathingMode[]>([
    {
      id: 'box',
      name: '箱式呼吸法 (Box Breathing)',
      slogan: '4 - 4 - 4 - 4',
      description: '這也是美國海軍海豹部隊常用的戰術呼吸法，用於在高壓下保持專注與冷靜。',
      steps: ['吸氣 4 秒', '暫停（屏氣） 4 秒', '呼氣 4 秒', '暫停（屏氣） 4 秒'],
      useCase: '提神、集中注意力、情緒重置。',
      phases: [
        { name: 'Inhale', duration: 4 },
        { name: 'Hold', duration: 4 },
        { name: 'Exhale', duration: 4 },
        { name: 'Hold', duration: 4 },
      ],
    },
    {
      id: '478',
      name: '4-7-8 呼吸法',
      slogan: '4 - 7 - 8',
      description: '這是公認最強效的「助眠與抗焦慮」呼吸法，強調長吐氣來啟動副交感神經。',
      steps: ['用鼻子吸氣 4 秒', '屏氣 7 秒', '用嘴巴緩慢吐氣 8 秒'],
      useCase: '嚴重失眠、極度焦慮。',
      warning: '這對心血管系統壓力稍大，若覺得屏氣 7 秒不舒服，請縮短時間或跳過此模式。',
      phases: [
        { name: 'Inhale', duration: 4 },
        { name: 'Hold', duration: 7 },
        { name: 'Exhale', duration: 8 },
      ],
    },
    {
      id: 'coherent',
      name: '共振呼吸法 (Coherent Breathing)',
      slogan: '5 - 5',
      description: '這是一種沒有屏氣的連續呼吸法，目標是將呼吸頻率降至每分鐘 5-6 次。這被科學證實最能提升心率變異度 (HRV)，有助於平衡血壓。',
      steps: ['吸氣 5 秒', '不憋氣，直接轉為呼氣 5 秒'],
      useCase: '日常保養血壓、心臟健康、放鬆但保持清醒。',
      phases: [
        { name: 'Inhale', duration: 5 },
        { name: 'Exhale', duration: 5 },
      ],
    },
    {
      id: '2to1',
      name: '2:1 放鬆呼吸法',
      slogan: '4 - 6',
      description: '原理很簡單：吸氣會興奮交感神經（心跳加速），吐氣會興奮副交感神經（心跳變慢）。延長吐氣時間就能讓身體放鬆。',
      steps: ['吸氣 4 秒', '呼氣 6 秒 (或更長)'],
      useCase: '騎完腳踏車後的緩和運動、感到緊張時。',
      phases: [
        { name: 'Inhale', duration: 4 },
        { name: 'Exhale', duration: 6 },
      ],
    },
  ]);

  selectedMode = signal<BreathingMode | null>(null);
  isRunning = signal(false);
  currentPhaseIndex = signal(0);
  countdown = signal(0);
  isMuted = signal(false);

  currentPhase = computed(() => {
    const mode = this.selectedMode();
    return mode ? mode.phases[this.currentPhaseIndex()] : null;
  });

  phaseInstruction = computed(() => {
    if (!this.isRunning() && this.selectedMode()) return '準備開始';
    const phase = this.currentPhase();
    if (!phase) return '';
    switch (phase.name) {
      case 'Inhale': return '吸氣';
      case 'Exhale': return '呼氣';
      case 'Hold': return '暫停';
      default: return '';
    }
  });

  animationState = computed(() => {
    if (!this.isRunning()) return 'idle';
    const phaseName = this.currentPhase()?.name;
    if (phaseName === 'Inhale') return 'inhale';
    if (phaseName === 'Exhale') return 'exhale';
    return 'hold';
  });

  isHoldAfterInhale = computed(() => {
    const mode = this.selectedMode();
    const phase = this.currentPhase();
    const state = this.animationState();

    if (!mode || state !== 'hold' || phase?.name !== 'Hold') return false;

    const index = this.currentPhaseIndex();
    if (index <= 0) return false;

    return mode.phases[index - 1]?.name === 'Inhale';
  });

  isHoldAfterExhale = computed(() => {
    const mode = this.selectedMode();
    const phase = this.currentPhase();
    const state = this.animationState();

    if (!mode || state !== 'hold' || phase?.name !== 'Hold') return false;

    const index = this.currentPhaseIndex();
    if (index <= 0) return false;

    return mode.phases[index - 1]?.name === 'Exhale';
  });

  timerManager = effect((onCleanup) => {
    if (this.isRunning()) {
      this.timerInterval = window.setInterval(() => {
        this.countdown.update(c => {
          if (c > 1) {
            return c - 1;
          } else {
            this.currentPhaseIndex.update(i => (i + 1) % (this.selectedMode()?.phases.length ?? 1));
            const nextPhase = this.currentPhase();
            return nextPhase ? nextPhase.duration : 0;
          }
        });
      }, 1000);

      onCleanup(() => {
        clearInterval(this.timerInterval);
      });
    }
  });

  soundTrigger = effect(() => {
    if (this.isRunning()) {
      const phase = this.currentPhase();
      if (phase) {
        this.playSoundForPhase(phase.name);
      }
    }
  });

  ngOnDestroy(): void {
    clearInterval(this.timerInterval);
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
    }
  }

  selectMode(mode: BreathingMode): void {
    this.selectedMode.set(mode);
    this.resetExercise();
  }

  goBack(): void {
    this.selectedMode.set(null);
  }

  toggleExercise(): void {
    if (!this.audioContext) {
      try {
        this.audioContext = new AudioContext();
      } catch (e) {
        console.error("Web Audio API is not supported in this browser.");
      }
    }

    if (this.audioContext && this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }

    if (this.isRunning()) {
      this.isRunning.set(false);
      this.resetExercise();
    } else {
      const firstPhase = this.selectedMode()?.phases[0];
      if (firstPhase) {
        this.currentPhaseIndex.set(0);
        this.countdown.set(firstPhase.duration);
        this.isRunning.set(true);
      }
    }
  }

  toggleMute(): void {
    this.isMuted.update(m => !m);
  }

  private playSoundForPhase(phaseName: Phase['name']): void {
    if (this.isMuted() || !this.audioContext) return;

    if (this.audioContext.state === 'suspended') {
        this.audioContext.resume();
    }

    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();
    const now = this.audioContext.currentTime;

    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    let freq = 440;
    const duration = 0.4; // Increased duration
    const peakVolume = 0.7; // Increased volume
    const attackTime = 0.02;

    switch (phaseName) {
      case 'Inhale':
        freq = 523.25; // C5 - Higher pitch for inhale
        break;
      case 'Exhale':
        freq = 261.63; // C4 - Lower pitch for exhale
        break;
      case 'Hold':
        freq = 392.00; // G4 - Neutral pitch for hold
        break;
    }

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(freq, now);

    // Audio envelope: short attack, then decay
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(peakVolume, now + attackTime);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, now + duration);

    oscillator.start(now);
    oscillator.stop(now + duration);
  }

  private resetExercise(): void {
    this.isRunning.set(false);
    this.currentPhaseIndex.set(0);
    const initialDuration = this.selectedMode()?.phases[0]?.duration ?? 0;
    this.countdown.set(initialDuration);
  }
}
